/**
 * DriftMonitorPage — Bank-to-Forecast Drift Monitor (Feature 7).
 * Alert banner, forecast vs actual trend line, daily drift table,
 * and today's bank summary.
 */

import React, { useMemo } from 'react';
import { Card, CardContent } from '../../components/ui';
import { useDriftMonitor, useForecastSummary } from './api';
import { formatCurrency, formatPct } from './utils';
import { cn } from '../../shared/lib/cn';
import {
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  ReferenceLine,
  ComposedChart,
} from 'recharts';
import type { ForecastDrift, DailyBankTotal } from './types';

const SEVERITY_BADGE: Record<string, string> = {
  low: 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900/30 dark:text-emerald-300',
  medium: 'bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-300',
  high: 'bg-orange-100 text-orange-800 dark:bg-orange-900/30 dark:text-orange-300',
  critical: 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300',
};

function formatDate(dateStr: string): string {
  const d = new Date(dateStr + 'T00:00:00');
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

function dayName(dateStr: string): string {
  const d = new Date(dateStr + 'T00:00:00');
  return d.toLocaleDateString('en-US', { weekday: 'short' });
}

export default function DriftMonitorPage() {
  const { data: summary } = useForecastSummary();
  const forecastId = summary?.forecast_id ?? '';
  const { data: monitor, isLoading } = useDriftMonitor(forecastId);

  const alerts = monitor?.alerts ?? [];
  const driftRecords = monitor?.drift_records ?? [];
  const dailyTotals = monitor?.daily_totals ?? [];

  const chartData = useMemo(() => {
    if (!driftRecords.length) return [];
    return driftRecords.map((d: ForecastDrift) => ({
      date: formatDate(d.drift_date),
      forecast: d.forecast_ending_cash,
      actual: d.actual_bank_total,
      drift: d.drift_amount,
    }));
  }, [driftRecords]);

  const todayBank = useMemo((): DailyBankTotal | null => {
    if (!dailyTotals.length) return null;
    return dailyTotals[dailyTotals.length - 1];
  }, [dailyTotals]);

  const covenantMin = useMemo(() => {
    if (!summary?.weeks?.length) return null;
    return 12_000_000;
  }, [summary]);

  if (isLoading || !forecastId) {
    return (
      <div className="space-y-6 mt-2">
        <Card>
          <CardContent className="p-8">
            <div className="animate-pulse space-y-4">
              <div className="h-6 w-48 bg-gray-200 dark:bg-dark-surface-2 rounded" />
              <div className="h-64 bg-gray-200 dark:bg-dark-surface-2 rounded" />
              <div className="h-40 bg-gray-200 dark:bg-dark-surface-2 rounded" />
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (!driftRecords.length && !dailyTotals.length) {
    return (
      <div className="space-y-6 mt-2">
        <Card>
          <CardContent className="p-8 text-center">
            <h2 className="text-lg font-semibold text-charcoal dark:text-gray-100 mb-2">
              Drift Monitor
            </h2>
            <p className="text-gray-500 dark:text-gray-400">
              No drift data available. Load daily bank balances and run the forecast to enable drift monitoring.
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  const interventionCount = alerts.filter(a => a.requires_intervention).length;

  return (
    <div className="space-y-6 mt-2">
      {/* Alert Banner */}
      {interventionCount > 0 && (
        <div className="rounded-lg border border-red-200 dark:border-red-800/50 bg-red-50 dark:bg-red-900/20 p-4">
          <div className="flex items-start gap-3">
            <div className="flex-shrink-0 mt-0.5">
              <span className="inline-flex items-center justify-center w-6 h-6 rounded-full bg-red-600 text-white text-xs font-bold">
                !
              </span>
            </div>
            <div>
              <h3 className="text-sm font-semibold text-red-800 dark:text-red-300">
                {interventionCount} Drift Alert{interventionCount > 1 ? 's' : ''} Requiring Intervention
              </h3>
              <p className="text-xs text-red-700 dark:text-red-400 mt-1">
                {alerts[0] && (
                  <>
                    Latest: {formatDate(alerts[0].drift_date)} — {alerts[0].severity} severity,{' '}
                    {formatCurrency(Math.abs(alerts[0].drift_amount), true)} {alerts[0].drift_direction} drift
                    {alerts[0].primary_driver && ` (${alerts[0].primary_driver})`}
                  </>
                )}
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Top row: Bank Summary + KPIs */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
        {todayBank && (
          <>
            <Card>
              <CardContent className="p-3">
                <p className="text-[10px] uppercase tracking-wider text-gray-500 dark:text-gray-400">Today's Balance</p>
                <p className="text-xl font-bold text-charcoal dark:text-gray-100">
                  {formatCurrency(todayBank.total_balance, true)}
                </p>
                <p className="text-[10px] text-gray-400">{formatDate(todayBank.balance_date)}</p>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-3">
                <p className="text-[10px] uppercase tracking-wider text-gray-500 dark:text-gray-400">Today's Inflows</p>
                <p className="text-xl font-bold text-emerald-600 dark:text-emerald-400">
                  {formatCurrency(todayBank.total_inflows, true)}
                </p>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-3">
                <p className="text-[10px] uppercase tracking-wider text-gray-500 dark:text-gray-400">Today's Outflows</p>
                <p className="text-xl font-bold text-red-600 dark:text-red-400">
                  {formatCurrency(todayBank.total_outflows, true)}
                </p>
              </CardContent>
            </Card>
          </>
        )}
        <Card>
          <CardContent className="p-3">
            <p className="text-[10px] uppercase tracking-wider text-gray-500 dark:text-gray-400">Alerts</p>
            <p className={cn('text-xl font-bold', interventionCount > 0 ? 'text-red-600 dark:text-red-400' : 'text-emerald-600 dark:text-emerald-400')}>
              {interventionCount}
            </p>
            <p className="text-[10px] text-gray-400">requiring intervention</p>
          </CardContent>
        </Card>
      </div>

      {/* Drift Chart */}
      {chartData.length > 0 && (
        <Card>
          <CardContent className="p-4">
            <h3 className="text-sm font-semibold text-charcoal dark:text-gray-100 mb-4">
              Forecast vs Actual Cash Position
            </h3>
            <ResponsiveContainer width="100%" height={320}>
              <ComposedChart data={chartData} margin={{ left: 10, right: 20, top: 10, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" className="opacity-30" />
                <XAxis
                  dataKey="date"
                  tick={{ fontSize: 11, fill: '#9ca3af' }}
                />
                <YAxis
                  tickFormatter={v => formatCurrency(v, true)}
                  tick={{ fontSize: 11, fill: '#9ca3af' }}
                />
                <Tooltip
                  formatter={(v: number, name: string) => [
                    formatCurrency(v),
                    name === 'forecast' ? 'Forecast' : name === 'actual' ? 'Actual Bank' : 'Drift',
                  ]}
                  contentStyle={{
                    backgroundColor: 'var(--color-surface, #fff)',
                    border: '1px solid #e5e7eb',
                    borderRadius: 8,
                    fontSize: 12,
                  }}
                />
                {covenantMin && (
                  <ReferenceLine
                    y={covenantMin}
                    stroke="#ef4444"
                    strokeDasharray="6 3"
                    label={{
                      value: 'Covenant Min',
                      position: 'insideTopRight',
                      fill: '#ef4444',
                      fontSize: 10,
                    }}
                  />
                )}
                <Line
                  type="monotone"
                  dataKey="forecast"
                  stroke="#3b82f6"
                  strokeWidth={2}
                  dot={false}
                  name="forecast"
                />
                <Line
                  type="monotone"
                  dataKey="actual"
                  stroke="#10b981"
                  strokeWidth={2}
                  dot={{ r: 3, fill: '#10b981' }}
                  name="actual"
                />
              </ComposedChart>
            </ResponsiveContainer>
            <div className="flex items-center justify-center gap-6 mt-3">
              <div className="flex items-center gap-1.5">
                <div className="w-3 h-0.5 bg-blue-500 rounded" />
                <span className="text-xs text-gray-500 dark:text-gray-400">Forecast</span>
              </div>
              <div className="flex items-center gap-1.5">
                <div className="w-3 h-0.5 bg-emerald-500 rounded" />
                <span className="text-xs text-gray-500 dark:text-gray-400">Actual Bank</span>
              </div>
              <div className="flex items-center gap-1.5">
                <div className="w-3 h-0.5 bg-red-500 rounded" style={{ borderTop: '1px dashed #ef4444' }} />
                <span className="text-xs text-gray-500 dark:text-gray-400">Covenant Min</span>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Daily Drift Table */}
      <Card>
        <CardContent className="p-0">
          <div className="px-4 pt-4 pb-2">
            <h3 className="text-sm font-semibold text-charcoal dark:text-gray-100">
              Daily Drift Detail
            </h3>
          </div>
          <div className="overflow-x-auto">
            <table className="min-w-full text-sm">
              <thead>
                <tr className="border-b border-gray-200 dark:border-dark-border bg-gray-50 dark:bg-dark-surface-2">
                  <th className="text-left py-2 px-3 font-medium text-gray-500 dark:text-gray-400">Date</th>
                  <th className="text-left py-2 px-3 font-medium text-gray-500 dark:text-gray-400">Day</th>
                  <th className="text-right py-2 px-3 font-medium text-gray-500 dark:text-gray-400">Forecast Cash</th>
                  <th className="text-right py-2 px-3 font-medium text-gray-500 dark:text-gray-400">Actual Bank</th>
                  <th className="text-right py-2 px-3 font-medium text-gray-500 dark:text-gray-400">Drift Amount</th>
                  <th className="text-right py-2 px-3 font-medium text-gray-500 dark:text-gray-400">Drift %</th>
                  <th className="text-center py-2 px-3 font-medium text-gray-500 dark:text-gray-400">Severity</th>
                  <th className="text-center py-2 px-3 font-medium text-gray-500 dark:text-gray-400">Intervention</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100 dark:divide-dark-border/50">
                {driftRecords.length === 0 ? (
                  <tr>
                    <td colSpan={8} className="text-center py-8 text-gray-500 dark:text-gray-400">
                      No drift records available.
                    </td>
                  </tr>
                ) : (
                  driftRecords.map((drift: ForecastDrift) => (
                    <tr
                      key={drift.id}
                      className={cn(
                        'hover:bg-gray-50 dark:hover:bg-dark-surface-2/50',
                        drift.requires_intervention && 'bg-red-50/50 dark:bg-red-900/10',
                      )}
                    >
                      <td className="py-2 px-3 text-charcoal dark:text-gray-200 whitespace-nowrap">
                        {formatDate(drift.drift_date)}
                      </td>
                      <td className="py-2 px-3 text-gray-500 dark:text-gray-400 text-xs whitespace-nowrap">
                        {dayName(drift.drift_date)}
                      </td>
                      <td className="py-2 px-3 text-right tabular-nums text-charcoal dark:text-gray-200 whitespace-nowrap">
                        {formatCurrency(drift.forecast_ending_cash, true)}
                      </td>
                      <td className="py-2 px-3 text-right tabular-nums text-charcoal dark:text-gray-200 whitespace-nowrap">
                        {formatCurrency(drift.actual_bank_total, true)}
                      </td>
                      <td className={cn(
                        'py-2 px-3 text-right tabular-nums font-medium whitespace-nowrap',
                        drift.drift_amount > 0
                          ? 'text-emerald-600 dark:text-emerald-400'
                          : drift.drift_amount < 0
                            ? 'text-red-600 dark:text-red-400'
                            : 'text-gray-500',
                      )}>
                        {drift.drift_amount > 0 ? '+' : ''}{formatCurrency(drift.drift_amount, true)}
                      </td>
                      <td className={cn(
                        'py-2 px-3 text-right tabular-nums text-xs whitespace-nowrap',
                        Math.abs(drift.drift_pct) > 10
                          ? 'text-red-600 dark:text-red-400 font-medium'
                          : Math.abs(drift.drift_pct) > 5
                            ? 'text-amber-600 dark:text-amber-400'
                            : 'text-gray-500 dark:text-gray-400',
                      )}>
                        {formatPct(drift.drift_pct)}
                      </td>
                      <td className="py-2 px-3 text-center">
                        <span className={cn(
                          'px-2 py-0.5 rounded text-xs font-medium',
                          SEVERITY_BADGE[drift.severity] || SEVERITY_BADGE.low,
                        )}>
                          {drift.severity}
                        </span>
                      </td>
                      <td className="py-2 px-3 text-center">
                        {drift.requires_intervention ? (
                          <span className="inline-flex items-center justify-center w-5 h-5 rounded-full bg-red-100 dark:bg-red-900/30">
                            <span className="text-red-600 dark:text-red-400 text-xs font-bold">!</span>
                          </span>
                        ) : (
                          <span className="text-gray-300 dark:text-gray-600">—</span>
                        )}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
